import { useEffect } from 'react';

import { LegalClientBodyType } from 'api/types/entity';
import { ConfirmationDialog } from 'components';
import { config } from 'config';
import { useTranslation } from 'libs/i18n';
import { DialogStateProps, Stack } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { DialogReasonChange } from 'modules/client/common/components';

import { Field, useFormContext } from '../bodyForm';

interface Props extends DialogStateProps {
  submitLoading?: boolean;
  editable?: boolean;
}

export const FormContent = ({
  isOpen,
  onClose,
  submitLoading,
  editable,
}: Props) => {
  const {
    values,
    setReadOnly,
    resetForm,
    validateForm,
    handleSubmit,
    setFieldValue,
  } = useFormContext();

  const { t } = useTranslation();

  const {
    open: openReason,
    onClose: onCloseReason,
    isOpen: isOpenReason,
  } = useDialog();

  const onConfirm = async () => {
    const validateResult = await validateForm();

    if (validateResult) {
      const invalidKeys = Object.keys(validateResult).filter(
        (key) => key !== 'reason'
      );

      if (invalidKeys.length) {
        if (config.IS_DEVELOPMENT) {
          // eslint-disable-next-line no-console
          console.info(invalidKeys);
        }
        return;
      }
    }

    openReason();
  };

  const onSaveReason = async (reason: string) => {
    setFieldValue('reason', reason);
    // need to call with a delay so that the field has time to record
    setTimeout(handleSubmit, 0);
  };

  useEffect(() => {
    resetForm();
    setReadOnly(!editable);
  }, [resetForm, isOpen, editable, setReadOnly]);

  useEffect(() => {
    if (!isOpen) {
      onCloseReason();
    }
  }, [isOpen, onCloseReason]);

  return (
    <>
      <ConfirmationDialog
        fullWidth
        confirmButtonLabel={t('common.save')}
        hideButtonConfirm={editable === false}
        isOpen={isOpen && !isOpenReason}
        maxWidth="sm"
        title={t('client.legal.companyStructure.bodyDialog.title')}
        onBack={onClose}
        onClose={onClose}
        onConfirm={onConfirm}
      >
        <Stack spacing={12}>
          <Field.Autocomplete
            showRequiredMark
            label={t('client.legal.companyStructure.bodyDialog.type.label')}
            name="type"
            optionLabelKey="label"
            options={Object.values(LegalClientBodyType).map((type) => ({
              label: t(`client.legal.companyStructure.bodyTypes.${type}`),
              value: type,
            }))}
            optionValueKey="value"
            placeholder={t(
              'client.legal.companyStructure.bodyDialog.type.label'
            )}
            variant="select"
          />
          {values.type?.value === LegalClientBodyType.Other && (
            <Field.Text
              fullWidth
              showRequiredMark
              label={t(
                'client.legal.companyStructure.bodyDialog.otherType.label'
              )}
              name="otherType"
              placeholder={t(
                'client.legal.companyStructure.bodyDialog.otherType.placeholder'
              )}
            />
          )}

          <Field.Text
            fullWidth
            helperTextAbsolute
            showRequiredMark
            label={t('client.legal.companyStructure.bodyDialog.name.label')}
            name="name"
            placeholder={t(
              'client.legal.companyStructure.bodyDialog.name.placeholder'
            )}
          />
        </Stack>
      </ConfirmationDialog>

      <DialogReasonChange
        isOpen={isOpen && isOpenReason}
        submitLoading={submitLoading}
        onClose={onCloseReason}
        onSave={onSaveReason}
      />
    </>
  );
};
